// Services CRUD API endpoints

import { baseApi } from '../baseApi';
import { API_ENDPOINTS } from '@/utils/constants';
import type {
  ServiceDetailResponse,
} from '@/types/services';

// API Response Types (matching OpenAPI spec)
interface ServiceCategory {
  id: string;
  name: string;
  icon_url: string;
}

interface ServiceRating {
  average: number;
  count: number;
}

interface ServiceItem {
  id: string;
  name: string;
  description: string;
  company_name: string;
  category: ServiceCategory | null;
  rating: ServiceRating;
  status: 'active';
  is_free: boolean;  // Free services bypass subscription and billing
  icon_url?: string;
  updated_at: string;
}

interface PaginationMetadata {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
}

interface AppliedFilters {
  search: string | null;
  category: string | null;
  sort: 'recent' | 'rating' | 'name';
}

interface ServiceListResponse {
  data: ServiceItem[];
  pagination: PaginationMetadata;
  filters: AppliedFilters;
}

interface ServiceListParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string; // UUID
  sort?: 'recent' | 'rating' | 'name';
}

// Review creation types
export interface CreateReviewRequest {
  rating: number; // 1-5 stars
  title?: string; // Optional review title
  comment?: string; // Optional review text
}

export interface CreateReviewResponse {
  id: string;
  user_id: string;
  service_id: string;
  rating: number;
  title?: string;
  comment?: string;
  created_at: string;
  updated_at?: string;
  is_verified: boolean;
  user: {
    email: string;
  };
}

// Review list types
export interface ReviewItem {
  id: string;
  user_id: string;
  service_id: string;
  rating: number;
  title?: string;
  comment?: string;
  created_at: string;
  updated_at?: string;
  is_verified: boolean;
  user: {
    name: string;
    email: string;
  };
}

export interface ReviewsListParams {
  page?: number;
  limit?: number;
  rating_filter?: number; // Filter by specific rating (1-5)
  sort?: 'recent' | 'rating_high' | 'rating_low'; // Sort options
}

export interface ReviewsListResponse {
  data: ReviewItem[];
  pagination: PaginationMetadata;
}

export const servicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get marketplace services (public endpoint)
    getServices: builder.query<ServiceListResponse, ServiceListParams>({
      query: ({ page = 1, limit = 20, search, category, sort = 'recent' }) => ({
        url: API_ENDPOINTS.SERVICES.LIST,
        params: {
          page,
          limit,
          search,
          category,
          sort,
        },
      }),
      providesTags: ['Service'],
    }),

    // Get a specific service by ID - returns detailed service information
    getService: builder.query<ServiceDetailResponse, string>({
      query: (id) => `/marketplace/services/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Service', id }],
    }),

    // Create a new subscription to a service plan
    createSubscription: builder.mutation<
      SubscriptionResponse,
      SubscriptionCreateRequest
    >({
      query: (body) => ({
        url: '/marketplace/subscriptions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Service', 'User'],
    }),

    // Cancel (unsubscribe) a subscription by ID
    cancelSubscription: builder.mutation<
      SubscriptionResponse,
      string
    >({
      query: (subscriptionId) => ({
        url: `/marketplace/subscriptions/${subscriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Service', 'User'],
    }),

    // Create a review for a service
    createReview: builder.mutation<
      CreateReviewResponse,
      { serviceId: string; review: CreateReviewRequest }
    >({
      query: ({ serviceId, review }) => ({
        url: `/marketplace/services/${serviceId}/user-reviews`,
        method: 'POST',
        body: review,
      }),
      invalidatesTags: (_result, _error, { serviceId }) => [
        { type: 'Service', id: serviceId },
        { type: 'Review', id: serviceId },
        'Service',
        'Review',
      ],
    }),

    // Get reviews for a specific service with filtering and sorting
    getServiceReviews: builder.query<
      ReviewsListResponse,
      { serviceId: string } & ReviewsListParams
    >({
      query: ({ serviceId, page = 1, limit = 10, rating_filter, sort = 'recent' }) => ({
        url: `/marketplace/services/${serviceId}/reviews`,
        params: {
          page,
          limit,
          rating_filter,
          sort,
        },
      }),
      providesTags: (_result, _error, { serviceId }) => [
        { type: 'Review', id: serviceId },
        'Review',
      ],
    }),
  }),
});

export const {
  useGetServicesQuery,
  useGetServiceQuery,
  useCreateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useCreateReviewMutation,
  useGetServiceReviewsQuery,
} = servicesApi;

// Export types for use in components
export type { ServiceItem, ServiceCategory, ServiceRating, ServiceListResponse, PaginationMetadata };

// Add types for subscription create request/response
export interface SubscriptionCreateRequest {
  plan_id: string;
  metadata?: Record<string, unknown>;
}

export interface SubscriptionResponse {
  id: string;
  user_id: string;
  plan_id: string;
  lago_subscription_id: string;
  status: 'active' | 'cancelled' | 'suspended' | 'expired';
  is_active: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}
